import React from 'react';
import { EventPageType } from '@graphql/generated-base';

import { LazyCoupleStory } from '../../../../widgets/CoupleStory/CoupleStory.lazy';
import { LazyFaqList } from '../../../../widgets/FaqList/FaqList.lazy';
import { LazyScheduleList } from '../../../../widgets/ScheduleList/ScheduleList.lazy';
import { LazyTravelList } from '../../../../widgets/TravelList/TravelList.lazy';
import { LazyWeddingPartyList } from '../../../../widgets/WeddingPartyList/WeddingPartyList.lazy';
import type { LayoutBrannanProps } from '../../LayoutBrannan';
import { Box } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { EventLogisticsBlock } from '../../../../components/EventLogisticsBlock';
import { LazyRegistryList } from '@apps/guest/packages/layout-engine/widgets/RegistryList/RegistryList.lazy';
import { CtaButtons } from '@apps/guest/packages/layout-engine/components/CtaButtons/CtaButtons';
import { LazyApps } from '@apps/guest/packages/layout-engine/widgets/Apps/Apps.lazy';
import { LazyCustom } from '@apps/guest/packages/layout-engine/widgets/Custom/Custom.lazy';
import { findEventPageByPageSlug } from '../../../layout.utils';
import { toCanonicalMigratedSlug } from '@shared/utils/migratedCustomPages';
import { EVENT_PAGE_SLUGS } from '@apps/guest/shared/constants/eventPageSlugs';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { ChunkErrorBoundary } from '@shared/components/ChunkErrorBoundary';
import { button2ToButtonsOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useGuestPageContentRedesign } from '@apps/guest/hooks/useGuestPageContentRedesign';
import { getEffectiveScheduleLayout } from '@shared/components/ScheduleLayoutSelector/ScheduleLayoutSelector.utils';

export type PreparedPages =
  | EventPageType.app
  | EventPageType.registry
  | EventPageType.travel
  | EventPageType.schedule
  | EventPageType.faq
  | EventPageType.vip
  | EventPageType.story
  | EventPageType.welcome
  | EventPageType.custom;

export const pageWidgets: Readonly<Record<PreparedPages, (props: LayoutBrannanProps) => React.ReactElement>> = {
  [EventPageType.custom]: props => {
    // Migrated registrylinks events keep their existing registry-links listing as the body.
    // Render it without the centering wrapper so the visual output is identical to the
    // legacy `EventPageType.registry` branch (any extra wrapper introduces padding drift on
    // flag rollback).
    const isMigratedRegistryPage = toCanonicalMigratedSlug(props.pageSlug) === EVENT_PAGE_SLUGS.Registry;
    if (isMigratedRegistryPage) {
      return (
        <ChunkErrorBoundary section="guest/widget/registryList">
          <LazyRegistryList
            eventId={props.event.id}
            eventHandle={props.eventHandle}
          />
        </ChunkErrorBoundary>
      );
    }
    return (
      <ChunkErrorBoundary section="guest/widget/custom">
        <Box
          data-testid={props.isWelcomePage ? 'page-custom-welcome' : 'page-custom'}
          display={'flex'}
          flexDirection={'column'}
          justifyContent="center"
          alignItems="center"
          width="100%"
        >
          <LazyCustom
            pageId={props.pageId || ''}
            eventId={props.event.id}
            pageSlug={props.pageSlug || ''}
            pageTitle={props.pageTitle}
            isBrannanLayout
            subTitle={props.subTitle}
            eventHandle={props.eventHandle}
            pages={props.event.pages}
          />
        </Box>
      </ChunkErrorBoundary>
    );
  },
  [EventPageType.app]: () => (
    <ChunkErrorBoundary section="guest/widget/apps">
      <Box
        data-testid={'page-apps'}
        width="100%"
      >
        <LazyApps data-testid={'page-app'} />
      </Box>
    </ChunkErrorBoundary>
  ),
  [EventPageType.faq]: props => (
    <ChunkErrorBoundary section="guest/widget/faqList">
      <LazyFaqList
        data-testid={'page-faq'}
        eventHandle={props.eventHandle}
      />
    </ChunkErrorBoundary>
  ),
  [EventPageType.registry]: props => (
    <ChunkErrorBoundary section="guest/widget/registryList">
      <LazyRegistryList
        eventId={props.event.id}
        eventHandle={props.eventHandle}
      />
    </ChunkErrorBoundary>
  ),
  [EventPageType.schedule]: props => {
    const schedulePage = findEventPageByPageSlug(props.event.pages, 'schedule');
    const { enabled: isRedesignEnabled } = useGuestPageContentRedesign();
    const effectiveLayoutType = getEffectiveScheduleLayout(schedulePage?.layoutType, isRedesignEnabled);
    const isCentered = effectiveLayoutType === 'centered';
    return (
      <ChunkErrorBoundary section="guest/widget/scheduleList">
        <LazyScheduleList
          data-testid={'page-schedule'}
          eventHandle={props.eventHandle}
          variant={isCentered ? 'stack' : 'inline'}
          eventId={props.event.id}
          layoutType={effectiveLayoutType}
          pageId={schedulePage?.id}
          {...(isCentered && {
            margin: '0 auto',
            maxWidth: { _: 'auto', md: pxToRem(440), lg: pxToRem(480) },
          })}
        />
      </ChunkErrorBoundary>
    );
  },
  [EventPageType.story]: props => (
    <ChunkErrorBoundary section="guest/widget/coupleStory">
      <LazyCoupleStory
        data-testid={'page-story'}
        eventHandle={props.eventHandle}
        eventPages={props.event.pages}
      />
    </ChunkErrorBoundary>
  ),
  [EventPageType.travel]: props => (
    <ChunkErrorBoundary section="guest/widget/travelList">
      <LazyTravelList
        data-testid={'page-travel'}
        eventHandle={props.eventHandle}
        eventId={props.event.id}
        useMiniNav={false}
        pages={props.event?.pages}
      />
    </ChunkErrorBoundary>
  ),
  [EventPageType.vip]: props => (
    <ChunkErrorBoundary section="guest/widget/weddingPartyList">
      <LazyWeddingPartyList
        data-testid={'page-vip'}
        graphicAccent={props.event.eventDesign?.accent}
        eventId={props.event.id}
        eventHandle={props.eventHandle}
      />
    </ChunkErrorBoundary>
  ),
  [EventPageType.welcome]: props => {
    const { eventInfo } = useEventInfo();
    return (
      <Box data-testid={'page-welcome'}>
        <EventLogisticsBlock
          location={props.event.info.location}
          date={props.event.info.finalizedDate}
          hideEventCountdown={eventInfo?.settings?.hideEventCountdown ?? false}
          pages={props.event.pages}
        >
          <GuestSiteTypographyOverride override={button2ToButtonsOverride}>
            <CtaButtons />
          </GuestSiteTypographyOverride>
        </EventLogisticsBlock>
      </Box>
    );
  },
};
